import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { ProfileConfig } from '../pages/Profile/Profile.props';
import { useUpdateBulkReports } from '../queries/report-query';
import {
  currentUserQueryKey,
  useGetCurrentUser,
  useUpdateProfileConfig,
} from '../queries/user-query';

/** The minimum a report must expose for these actions to work on it. */
export interface ActionableReport {
  id: number;
  code: string;
  name: string;
  isDefault?: boolean;
  /** Report-type key — pinning and defaults are both scoped to one page. */
  pageName: string;
}

export interface UseReportActionsReturn {
  /** Whether the report is pinned as a tab on its own list page. */
  isPinned: (report: ActionableReport) => boolean;
  /** Pin or unpin, writing to that report's page rather than the current one. */
  togglePin: (report: ActionableReport) => void;
  /**
   * Make this the default report for its page, or clear it if it already is.
   * `siblings` are the other reports of the same type, needed to find the
   * outgoing default — only one report per page may hold the flag.
   */
  toggleDefault: (report: ActionableReport, siblings: ActionableReport[]) => Promise<void>;
  isUpdatingDefault: boolean;
}

/**
 * Pin and default actions that work on a report from anywhere, not just from
 * the list page that report belongs to.
 *
 * `usePinnedReports` is deliberately scoped to a single page — it backs one tab
 * strip. My Reports spans every page, so it needs to write into whichever page's
 * key the report belongs to. Both write the same profile-config blob and both
 * merge against the freshest cached copy, so they can coexist.
 */
export const useReportActions = (): UseReportActionsReturn => {
  const getCurrentUser = useGetCurrentUser();
  const userId = getCurrentUser.data?.id;
  const config = getCurrentUser.data?.config;
  const updateProfileConfig = useUpdateProfileConfig(userId ?? 0);
  const bulkUpdateReports = useUpdateBulkReports();
  const queryClient = useQueryClient();

  const pinnedByPage = useMemo(() => config?.pinnedReports ?? {}, [config]);

  const isPinned = useCallback(
    (report: ActionableReport) => (pinnedByPage[report.pageName] ?? []).includes(report.code),
    [pinnedByPage]
  );

  const togglePin = useCallback(
    (report: ActionableReport) => {
      if (!userId) return;
      const key = currentUserQueryKey();
      // Merge against the FRESHEST cached config, not this render's closure —
      // the tab strips write the same blob, and a stale copy would clobber a
      // pin made on another page (or in another tab) since this render.
      const cached = queryClient.getQueryData<{ config?: ProfileConfig }>(key);
      const freshest: ProfileConfig = cached?.config ?? config ?? {};
      const current = freshest.pinnedReports?.[report.pageName] ?? [];
      const next = current.includes(report.code)
        ? current.filter((c) => c !== report.code)
        : [...current, report.code];
      const nextConfig: ProfileConfig = {
        ...freshest,
        pinnedReports: { ...(freshest.pinnedReports ?? {}), [report.pageName]: next },
      };

      // Write the merge back into the cache before firing the request. Without
      // this, a second toggle before the save lands re-reads the pre-toggle
      // config and drops the first pin — and the icon would not flip until a
      // refetch. Rolled back if the save fails, so the UI can't claim a pin the
      // server never took.
      queryClient.setQueryData(key, (old: unknown) =>
        old ? { ...(old as object), config: nextConfig } : old
      );

      updateProfileConfig.mutate(
        { config: nextConfig },
        { onError: () => queryClient.setQueryData(key, cached) }
      );
    },
    [userId, config, queryClient, updateProfileConfig]
  );

  const toggleDefault = useCallback(
    async (report: ActionableReport, siblings: ActionableReport[]) => {
      if (report.isDefault) {
        await bulkUpdateReports.mutateAsync([{ id: report.id, isDefault: false }]);
        return;
      }
      // Only one default per page, so the outgoing one is cleared in the same
      // call — a separate request could leave two defaults if the second failed.
      const outgoing = siblings.find(
        (r) => r.isDefault && r.id !== report.id && r.pageName === report.pageName
      );
      const updates = [{ id: report.id, isDefault: true }];
      if (outgoing) updates.unshift({ id: outgoing.id, isDefault: false });
      await bulkUpdateReports.mutateAsync(updates);
    },
    [bulkUpdateReports]
  );

  return { isPinned, togglePin, toggleDefault, isUpdatingDefault: bulkUpdateReports.isPending };
};
